import React, { useState } from 'react';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { V2Card } from '@uhg-abyss/web/ui/Card';
import { V2Button } from '@uhg-abyss/web/ui/Button';
import { V2Checkbox } from '@uhg-abyss/web/ui/Checkbox';
import { useFormV2 } from '@uhg-abyss/web/hooks/useForm';
import { useTinRegistration } from '@src/state/tinRegistration';
import {
  StaticAddressSection,
  SecondaryAddressCard,
} from './LocationAddressSections';
import { MAX_SECONDARY_ADDRESSES } from '../constants';

const SECONDARY_SLOT_IDS = Array.from(
  { length: MAX_SECONDARY_ADDRESSES },
  (_, i) => i
);

const SectionText = styled('p', {
  margin: '8px 0 8px',
  fontSize: '16px',
  fontWeight: 600,
});

const CardsWrapper = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
});

const CheckboxRow = styled('div', {
  margin: '12px 4px 0',
});

const CheckboxStack = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  margin: '12px 4px 0',
});

const CheckboxItem = styled('div', {
  cursor: 'pointer',
  '& input': {
    cursor: 'pointer',
  },
  '&[data-disabled=true]': {
    cursor: 'not-allowed',
    '& input': {
      cursor: 'not-allowed',
    },
  },
});

const isAddressEligibleForCopy = (address = {}) =>
  !!address?.streetLine1?.trim() &&
  !!address?.city?.trim() &&
  !!address?.city?.match(/[a-zA-Z]/) &&
  !!address?.state &&
  /^\d{5}$/.test(address?.zipCode || '');

const getNextChecked = (value, currentChecked) => {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value?.target?.checked === 'boolean') {
    return value.target.checked;
  }

  return !currentChecked;
};

const copyAddressFields = ({ form, sourcePath, targetPath }) => {
  const values = form.getValues();
  const sourceAddress =
    sourcePath.split('.').reduce((acc, key) => acc?.[key], values) || {};

  // Update only target address fields so existing validation state for
  // unrelated models (for example proof uploads) is preserved.
  form.setValue(
    `address.${targetPath}.streetLine1`,
    sourceAddress.streetLine1 || ''
  );
  form.setValue(
    `address.${targetPath}.streetLine2`,
    sourceAddress.streetLine2 || ''
  );
  form.setValue(`address.${targetPath}.city`, sourceAddress.city || '');
  form.setValue(`address.${targetPath}.state`, sourceAddress.state || '');
  form.setValue(`address.${targetPath}.zipCode`, sourceAddress.zipCode || '');
};

const getSecondaryProofModel = (addrId) =>
  `address.secondaryServicing[${addrId}].proof`;

const SameAsServicingCheckbox = ({ onAddressCopied }) => {
  const form = useFormV2();
  const [checked, setChecked] = useState(false);

  const streetLine1 = form.watch('address.primaryServicing.streetLine1');
  const city = form.watch('address.primaryServicing.city');
  const state = form.watch('address.primaryServicing.state');
  const zipCode = form.watch('address.primaryServicing.zipCode');

  const isEnabled =
    !!streetLine1?.trim() &&
    !!city?.trim() &&
    !!city.match(/[a-zA-Z]/) &&
    !!state &&
    /^\d{5}$/.test(zipCode);

  const handleChange = (newChecked) => {
    const nextChecked = getNextChecked(newChecked, checked);
    setChecked(nextChecked);

    if (nextChecked) {
      copyAddressFields({
        form,
        sourcePath: 'address.primaryServicing',
        targetPath: 'primaryBilling',
      });
      onAddressCopied?.();
    }
  };

  return (
    <CheckboxRow>
      <CheckboxItem data-disabled={!isEnabled}>
        <V2Checkbox
          id="same-as-servicing-to-billing"
          label="Same as servicing address"
          checked={checked}
          isDisabled={!isEnabled}
          onChange={handleChange}
        />
      </CheckboxItem>
    </CheckboxRow>
  );
};

const SameAsAddressOptions = ({ onAddressCopied }) => {
  const form = useFormV2();
  const [selectedSource, setSelectedSource] = useState('');

  const servicingAddress = {
    streetLine1: form.watch('address.primaryServicing.streetLine1'),
    streetLine2: form.watch('address.primaryServicing.streetLine2'),
    city: form.watch('address.primaryServicing.city'),
    state: form.watch('address.primaryServicing.state'),
    zipCode: form.watch('address.primaryServicing.zipCode'),
  };

  const billingAddress = {
    streetLine1: form.watch('address.primaryBilling.streetLine1'),
    streetLine2: form.watch('address.primaryBilling.streetLine2'),
    city: form.watch('address.primaryBilling.city'),
    state: form.watch('address.primaryBilling.state'),
    zipCode: form.watch('address.primaryBilling.zipCode'),
  };

  const isServicingEnabled = isAddressEligibleForCopy(servicingAddress);
  const isBillingEnabled = isAddressEligibleForCopy(billingAddress);

  const handleServicingToggle = (newChecked) => {
    const isCurrentlySelected = selectedSource === 'servicing';
    const nextChecked = getNextChecked(newChecked, isCurrentlySelected);

    if (!nextChecked) {
      setSelectedSource((curr) => (curr === 'servicing' ? '' : curr));
      return;
    }

    setSelectedSource('servicing');
    copyAddressFields({
      form,
      sourcePath: 'address.primaryServicing',
      targetPath: 'primaryRemittance',
    });
    onAddressCopied?.();
  };

  const handleBillingToggle = (newChecked) => {
    const isCurrentlySelected = selectedSource === 'billing';
    const nextChecked = getNextChecked(newChecked, isCurrentlySelected);

    if (!nextChecked) {
      setSelectedSource((curr) => (curr === 'billing' ? '' : curr));
      return;
    }

    setSelectedSource('billing');
    copyAddressFields({
      form,
      sourcePath: 'address.primaryBilling',
      targetPath: 'primaryRemittance',
    });
    onAddressCopied?.();
  };

  return (
    <CheckboxStack>
      <CheckboxItem data-disabled={!isServicingEnabled}>
        <V2Checkbox
          id="same-as-servicing-to-remittance"
          label="Same as servicing address"
          checked={selectedSource === 'servicing'}
          isDisabled={!isServicingEnabled}
          onChange={handleServicingToggle}
        />
      </CheckboxItem>
      <CheckboxItem data-disabled={!isBillingEnabled}>
        <V2Checkbox
          id="same-as-billing-to-remittance"
          label="Same as billing address"
          checked={selectedSource === 'billing'}
          isDisabled={!isBillingEnabled}
          onChange={handleBillingToggle}
        />
      </CheckboxItem>
    </CheckboxStack>
  );
};

const getPrimaryAddressSections = (onAddressCopied) => [
  {
    sectionKey: 'primaryServicing',
    title: 'Primary servicing address',
    helperText: "Provide your practice's primary servicing address.",
    helperText2:
      'Primary servicing location cannot be a P.O. Box. All addresses will be checked and verified.',
    modelPrefix: 'address.primaryServicing',
    proofModel: 'address.primaryServicingProof',
    proofLabel: 'Proof of servicing address',
  },
  {
    sectionKey: 'primaryBilling',
    title: 'Primary billing address',
    helperText: "Provide your practice's primary billing address.",
    modelPrefix: 'address.primaryBilling',
    proofModel: 'address.primaryBillingProof',
    proofLabel: 'Proof of billing address',
    beforeFields: <SameAsServicingCheckbox onAddressCopied={onAddressCopied} />,
  },
  {
    sectionKey: 'primaryRemittance',
    title: 'Primary remittance address',
    helperText: "Provide your practice's primary remittance address.",
    modelPrefix: 'address.primaryRemittance',
    proofModel: 'address.primaryRemittanceProof',
    proofLabel: 'Proof of remittance address',
    beforeFields: <SameAsAddressOptions onAddressCopied={onAddressCopied} />,
  },
];

export const LocationInformation = ({
  secondaryIds,
  setSecondaryIds,
  doc360UploadErrors,
  setDoc360UploadErrors,
  locationAddressErrors,
  addrValidationCount,
  reValidateAddresses,
}) => {
  const form = useFormV2();

  const triggerAll = () => {
    reValidateAddresses({ fromBlur: true });
  };

  const triggerStaticSection = (sectionKey) => () => {
    reValidateAddresses({
      fromBlur: true,
      scope: { type: 'static', key: sectionKey },
    });
  };

  const triggerSecondaryCard = (addrId) => () => {
    reValidateAddresses({
      fromBlur: true,
      scope: { type: 'secondary', id: addrId },
    });
  };

  const { setDocumentId } = useTinRegistration.actions();
  const staticAddressErrors = locationAddressErrors?.static;
  const servicingAddressErrors = locationAddressErrors?.secondary;

  const isMaxReached = secondaryIds.length >= MAX_SECONDARY_ADDRESSES;

  const handleAddSecondary = () => {
    setSecondaryIds((curr) => {
      const nextId = SECONDARY_SLOT_IDS.find((id) => !curr.includes(id));
      if (nextId === undefined) return curr;
      return [...curr, nextId];
    });
  };

  const handleRemoveSecondary = (addrId) => {
    const abyssFormVals = form.getValues();
    delete abyssFormVals.address?.secondaryServicing?.[addrId];
    form.reset({ ...abyssFormVals });

    setDocumentId({
      id: getSecondaryProofModel(addrId),
      value: undefined,
    });

    setDoc360UploadErrors((curr) => {
      const updated = { ...curr };
      delete updated[getSecondaryProofModel(addrId)];
      return updated;
    });

    const nextSecondaryIds = secondaryIds.filter((id) => id !== addrId);
    setSecondaryIds(nextSecondaryIds);

    reValidateAddresses({
      fromBlur: true,
      secondaryIdsOverride: nextSecondaryIds,
    });
  };

  return (
    <>
      {getPrimaryAddressSections(triggerAll).map((section) => (
        <StaticAddressSection
          key={section.sectionKey}
          title={section.title}
          helperText={section.helperText}
          helperText2={section.helperText2}
          beforeFields={section.beforeFields}
          modelPrefix={section.modelPrefix}
          proofModel={section.proofModel}
          proofLabel={section.proofLabel}
          doc360Error={doc360UploadErrors?.[section.proofModel]}
          setDoc360UploadErrors={setDoc360UploadErrors}
          hasError={
            staticAddressErrors?.[section.sectionKey] ||
            !!doc360UploadErrors?.[section.proofModel]
          }
          addrValidationCount={addrValidationCount}
          triggerAll={triggerStaticSection(section.sectionKey)}
        />
      ))}

      <V2Card>
        <Layout.Group alignLayout="between">
          <h4>Add secondary servicing address (optional)</h4>

          <V2Button
            variant="outline"
            isDisabled={isMaxReached}
            onClick={handleAddSecondary}
            size="$sm"
          >
            Add address
          </V2Button>
        </Layout.Group>

        <SectionText>
          Provide up to {MAX_SECONDARY_ADDRESSES} additional servicing addresses
          you may have. All addresses used to submit claims are required.
        </SectionText>

        {secondaryIds.length > 0 && (
          <CardsWrapper>
            {secondaryIds.map((id, index) => (
              <SecondaryAddressCard
                key={id}
                addrId={id}
                addrNumber={index + 1}
                totalAddresses={secondaryIds.length}
                onAddrRemove={handleRemoveSecondary}
                hasError={servicingAddressErrors?.has(id)}
                doc360Error={doc360UploadErrors?.[getSecondaryProofModel(id)]}
                setDoc360UploadErrors={setDoc360UploadErrors}
                addrValidationCount={addrValidationCount}
                triggerAll={triggerSecondaryCard(id)}
              />
            ))}
          </CardsWrapper>
        )}
      </V2Card>
    </>
  );
};
